import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

function handleApiError(error: unknown): ApiError {
	if (axios.isAxiosError(error)) {
		const status = error.response?.status || 500;
		return {
			status,
			message:
				error.response?.data?.message ||
				error.response?.data?.Message ||
				error.response?.data?.title ||
				error.response?.data?.Title ||
				error.response?.data ||
				"An unexpected error occurred. Please try again later",
			errorCode: error.response?.data?.code || "UNKNOWN_ERROR",
			originalError: error,
			validationErrors: status === 400 ? error.response?.data?.errors || null : null,
		};
	}
	return {
		status: 500,
		message: "Unknown error occurred",
		errorCode: "UNHANDLED_ERROR",
		originalError: error,
		validationErrors: null,
	};
}

export class Api {
	private client: AxiosInstance;
	private static currentSignal: AbortSignal | null = null;

	static runWithSignal<T>(signal: AbortSignal, fn: () => Promise<T>): Promise<T> {
		const prev = Api.currentSignal;
		Api.currentSignal = signal;
		return fn().finally(() => {
			Api.currentSignal = prev;
		});
	}

	constructor(
		baseURL: string,
		headers: Record<string, string> = {},
		setupInterceptors?: (client: AxiosInstance) => void,
	) {
		this.client = axios.create({
			baseURL,
			headers: { "Content-Type": "application/json", ...headers },
		});

		// Allow custom interceptors to be defined externally
		if (setupInterceptors) {
			setupInterceptors(this.client);
		}
	}

	async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
		try {
			const mergedConfig: AxiosRequestConfig = {
				...config,
				signal: config?.signal ?? Api.currentSignal ?? undefined,
			};
			const response: AxiosResponse<T> = await this.client.get(endpoint, mergedConfig);
			return response.data;
		} catch (error) {
			throw handleApiError(error);
		}
	}

	async post<T>(endpoint: string, body: unknown, config?: AxiosRequestConfig): Promise<T> {
		try {
			const response: AxiosResponse<T> = await this.client.post(endpoint, body, config);
			return response.data;
		} catch (error) {
			throw handleApiError(error);
		}
	}

	async put<T>(endpoint: string, body: unknown, config?: AxiosRequestConfig): Promise<T> {
		try {
			const response: AxiosResponse<T> = await this.client.put(endpoint, body, config);
			return response.data;
		} catch (error) {
			throw handleApiError(error);
		}
	}

	async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
		try {
			const response: AxiosResponse<T> = await this.client.delete(endpoint, config);
			return response.data;
		} catch (error) {
			throw handleApiError(error);
		}
	}
}
